import { IS_LIVE } from '@/_config/env';

const ERROR_TYPE   = 'ENNIX-MAIN-WEBSITE';
const BACKEND_URL  = 'https://api.ennixelectronics.com';
const TIMEOUT_MS   = 5000;

/**
 * Log any error — works in both browser and Node/server-action context.
 *
 * @param {object} opts
 * @param {string}  opts.message    - Error message (required)
 * @param {string} [opts.stack]     - Stack trace
 * @param {string} [opts.source]    - File / component name
 * @param {string} [opts.endpoint]  - API endpoint that failed
 * @param {string} [opts.method]    - Function / handler name
 * @param {string} [opts.userId]    - Authenticated user id (if known)
 * @param {number} [opts.statusCode]- HTTP status code if applicable
 * @param {object} [opts.meta]      - Any extra context
 */
export async function logError({
  message,
  stack,
  source,
  endpoint,
  method,
  userId,
  statusCode,
  meta,
} = {}) {
  if (!message) return;
  if (!IS_LIVE) return;

  const payload = {
    message:   String(message).slice(0, 1000),
    stack:     stack     ? String(stack).slice(0, 3000)  : undefined,
    source:    source    ? String(source).slice(0, 200)  : undefined,
    endpoint:  endpoint  ? String(endpoint).slice(0, 200): undefined,
    method:    method    ? String(method).slice(0, 200)  : undefined,
    userId:    userId    ?? undefined,
    statusCode: statusCode ?? undefined,
    meta:      meta      ?? undefined,
    errorType: ERROR_TYPE,
  };

  try {
    const isBrowser = typeof window !== 'undefined';

    // Browser → Next.js proxy route (adds auth userId from cookie server-side)
    // Server  → backend directly (no cookie available in logger context)
    const url = isBrowser ? '/api/error-logs' : `${BACKEND_URL}/error-logs`;

    const signal = typeof AbortSignal?.timeout === 'function'
      ? AbortSignal.timeout(TIMEOUT_MS)
      : undefined;

    await fetch(url, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(payload),
      signal,
    });
  } catch {
    // never throw — error reporting must never crash the app
  }
}

/**
 * Helper to build a logError call from a caught Error object.
 */
export function logCaughtError(err, context = {}) {
  return logError({
    message:    err?.message ?? String(err),
    stack:      err?.stack,
    statusCode: err?.response?.status ?? err?.status,
    ...context,
  });
}
